import React, { useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { usePagedQuery } from "../../hooks/usePagedQuery";

type User = {
  id: number;
  email: string;
  name: string;
  phone?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  profile?: {
    id: number;
    name: string;
  };
};

type AccessProfile = {
  id: number;
  name: string;
};

export const UsersPage: React.FC = () => {
  const { client } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [profileId, setProfileId] = useState<number | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [sendInvite, setSendInvite] = useState(false);

  const [profiles, setProfiles] = useState<AccessProfile[]>([]);

  const query = usePagedQuery<User>({
    endpoint: "/users",
    filters: {},
  });

  React.useEffect(() => {
    const loadProfiles = async () => {
      const response = await client.get("/modules/access-profiles?take=100&skip=0");
      if (response.ok) {
        const data = (await response.json()) as { data: AccessProfile[] };
        setProfiles(data.data || []);
      }
    };
    loadProfiles();
  }, [client]);

  const resetForm = () => {
    setEmail("");
    setName("");
    setPassword("");
    setPhone("");
    setProfileId(null);
    setIsActive(true);
    setSendInvite(false);
    setEditingUser(null);
    setError(null);
  };

  const handleCreate = () => {
    resetForm();
    setModalMode("create");
    setShowModal(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setModalMode("edit");
    setEmail(user.email);
    setName(user.name);
    setPhone(user.phone || "");
    setProfileId(user.profile?.id || null);
    setIsActive(user.isActive);
    setPassword(""); // Don't pre-fill password
    setSendInvite(false);
    setShowModal(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const payload = {
        email,
        name,
        phone: phone || null,
        profileId,
        isActive,
        ...(modalMode === "create" && {
          password: sendInvite ? (password || undefined) : password,
          sendInvite,
        }),
      };

      const response = modalMode === "create"
        ? await client.post("/users", payload)
        : await client.put(`/users/${editingUser?.id}`, payload);

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Falha ao salvar usuário.");
        return;
      }

      setShowModal(false);
      resetForm();
      await query.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      const response = await client.put(`/users/${user.id}`, {
        isActive: !user.isActive,
      });

      if (!response.ok) {
        throw new Error("Falha ao atualizar status");
      }

      await query.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao atualizar status");
    }
  };

  const handleSendPasswordReset = async (user: User) => {
    try {
      const response = await client.post(`/users/${user.id}/reset-password`, {});

      if (!response.ok) {
        throw new Error("Falha ao enviar link");
      }

      const data = await response.json();
      if (data.resetLink) {
        alert(`Link de reset enviado!\n\n${data.resetLink}\n\nEm producao, este link seria enviado por e-mail.`);
      } else {
        alert("Link de reset enviado. Verifique o e-mail do usuario.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao enviar link");
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Gestão de Usuários</h4>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Novo Usuário
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} />
        </div>
      )}

      {query.loading && <div className="text-center py-3">Carregando...</div>}

      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Perfil</th>
              <th>Telefone</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {query.rows.map((user) => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>
                  {user.profile ? user.profile.name : "-"}
                </td>
                <td>{user.phone || "-"}</td>
                <td>
                  <span className={`badge ${user.isActive ? "bg-success" : "bg-secondary"}`}>
                    {user.isActive ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td>
                  <div className="btn-group btn-group-sm" role="group">
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => handleEdit(user)}
                    >
                      Editar
                    </button>
                    <button
                      className={`btn btn-outline-${user.isActive ? "warning" : "success"}`}
                      onClick={() => handleToggleActive(user)}
                    >
                      {user.isActive ? "Desativar" : "Ativar"}
                    </button>
                    <button
                      className="btn btn-outline-info"
                      onClick={() => handleSendPasswordReset(user)}
                    >
                      Reset Senha
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          Mostrando {query.rows.length > 0 ? query.skip + 1 : 0} -{" "}
          {Math.min(query.skip + query.take, query.total)} de {query.total} resultados
        </div>
        <div className="btn-group">
          <button
            className="btn btn-outline-secondary"
            disabled={query.skip === 0}
            onClick={() => query.setSkip(Math.max(0, query.skip - query.take))}
          >
            Anterior
          </button>
          <button
            className="btn btn-outline-secondary"
            disabled={query.skip + query.take >= query.total}
            onClick={() => query.setSkip(query.skip + query.take)}
          >
            Próximo
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalMode === "create" ? "Novo Usuário" : "Editar Usuário"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={saving}
                />
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Nome *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  {modalMode === "create" && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">Senha</label>
                        <input
                          type="password"
                          className="form-control"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required={!sendInvite}
                        />
                      </div>
                      <div className="form-check form-switch mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={sendInvite}
                          onChange={(e) => setSendInvite(e.target.checked)}
                          id="sendInvite"
                        />
                        <label className="form-check-label" htmlFor="sendInvite">
                          Enviar convite para definir senha
                        </label>
                      </div>
                    </>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Telefone</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Perfil de Acesso *</label>
                    <select
                      className="form-select"
                      value={profileId || ""}
                      onChange={(e) => setProfileId(e.target.value ? Number(e.target.value) : null)}
                      required
                    >
                      <option value="">Selecione um perfil...</option>
                      {profiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name}
                        </option>
                      ))}
                    </select>
                    {profiles.length === 0 && (
                      <small className="text-muted">
                        Nenhum perfil de acesso encontrado. Execute o seed do banco de dados.
                      </small>
                    )}
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      id="isActive"
                    />
                    <label className="form-check-label" htmlFor="isActive">
                      Usuário ativo
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Salvando...
                      </>
                    ) : (
                      "Salvar"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

